// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import createIdbStorage from 'redux-persist-indexeddb-storage';

import { createWebSocketMiddleware } from '../../web-utils/src/store/Websocket/WebsocketMiddleware';
import { appLogoutReset } from './globalActions';
import wsEventHandlers from './websocket/WebsocketHandler';

import userReducer from './user/userSlice';
import studioReducer from './studio/studioSlice';
import scenesReducer from './scenes/scenesSlice';
import companiesReducer from './companies/companiesSlice';
import showsReducer from './shows/showsSlice';
import equipmentReducer from './equipment/equipmentSlice';
import scriptsReducer from './scripts/scriptsSlices';
import userPreferencesReducer from './userPreferences/userPreferencesSlice';

// shared reducer for websocket state management
import webSocketSlice from '../../web-utils/src/store/Websocket/WebsocketSlice';

const BUILD_ID = process.env.GIT_BUILD || '1';

// IndexedDB for larger data caching (ex.equipment)
const idbStorage = createIdbStorage({
  name: 'mosys-db', // IndexedDB database name
  storeName: 'equipment', // Object store name
});

const equipmentPersistConfig = {
  key: 'equipment',
  storage: idbStorage,
  version: BUILD_ID, // clear redux cached state on commit change
  migrate: async inboundState => {
    // If the version doesn't match, return the original state
    const prev = inboundState?._persist?.version;
    return prev === BUILD_ID ? inboundState : undefined;
  },
};

const persistedEquipmentReducer = persistReducer(equipmentPersistConfig, equipmentReducer);

// Combine multiple slices into a single reducer
const appReducer = combineReducers({
  user: userReducer,
  companies: companiesReducer,
  shows: showsReducer,
  equipment: persistedEquipmentReducer, // Use the persisted reducer for equipment
  scripts: scriptsReducer,
  scenes: scenesReducer,
  studio: studioReducer,
  userPreferences: userPreferencesReducer,
  // web-utils shared state
  webSocket: webSocketSlice,
});

const rootReducer = (state, action) => {
  if (action.type === appLogoutReset.type) {
    // Preserve userPreferences cached state on logout, reset all other slices to initial state
    state = { userPreferences: state?.userPreferences };
  }

  return appReducer(state, action);
};

// Create websocket middleware with shared event handlers
const websocketMiddleware = createWebSocketMiddleware(wsEventHandlers);

// Persist configuration for redux-persist(caching redux state in local storage)
const persistConfig = {
  key: 'root',
  version: BUILD_ID, // clear redux cached state on deployment
  migrate: async inboundState => {
    // If the version doesn't match, return the original state
    const prev = inboundState?._persist?.version;
    return prev === BUILD_ID ? inboundState : undefined;
  },
  storage, // localStorage for smaller data caching
  whitelist: ['companies', 'shows', 'studio', 'scripts', 'scenes', 'userPreferences'], // Only cache reducers in this list
};

// Create a persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure the store with the persisted reducer
export const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      immutableCheck: false, // Disable ImmutableStateInvariantMiddleware
      serializableCheck: false, // Disable SerializableStateInvariantMiddleware
    }).concat(websocketMiddleware),
});

// Export persistor for use in your application
export const persistor = persistStore(store);
